/**
 * Weekly Calendar Component
 * Displays a week view sourced from local planner data.
 */
export class WeeklyCalendar {
  constructor(container, dataManager) {
    this.container = container;
    this.data = dataManager;
    this.currentWeek = new Date();
    this.events = [];
    this.tasks = [];
    this.unsubscribeFns = [];
    this.calendarInsights = this.calculateCalendarSummary([]);

    this.init();
  }

  init() {
    console.log('WeeklyCalendar: Container found:', !!this.container);
    if (this.container) {
      this.render();
      this.loadEvents();

      const unsubscribeEvents = this.data?.subscribe?.('calendar_events', () => this.refresh());
      const unsubscribeTasks = this.data?.subscribe?.('enoughTasks', () => this.refresh());
      if (typeof unsubscribeEvents === 'function') this.unsubscribeFns.push(unsubscribeEvents);
      if (typeof unsubscribeTasks === 'function') this.unsubscribeFns.push(unsubscribeTasks);
    } else {
      console.error('WeeklyCalendar: Container not found!');
    }
  }

  render() {
    const weekDates = this.getWeekDates(this.currentWeek);
    const weekStart = weekDates[0];
    const weekEnd = weekDates[6];

    this.container.innerHTML = `
      <div class="weekly-calendar">
        <div class="calendar-header">
          <div class="calendar-title">
            <h3>This Week & Focus Sessions</h3>
            <div class="week-range">
              ${this.formatWeekRange(weekStart, weekEnd)}
            </div>
          </div>
          <div class="calendar-actions">
            <button class="btn btn--small btn--ghost prev-week-btn" title="Previous week">
              <span>◀</span>
            </button>
            <button class="btn btn--small btn--primary plan-btn" title="Plan focus sessions">
              <span>🔥</span>
              Plan
            </button>
            <button class="btn btn--small btn--ghost next-week-btn" title="Next week">
              <span>▶</span>
            </button>
          </div>
        </div>

        ${this.renderCalendarInsights()}

        <div class="calendar-week">
          ${weekDates.map((date, index) => this.renderDayCard(date, index)).join('')}
        </div>
      </div>
    `;

    this.attachEventListeners();

    // Debug: Check if navigation buttons were created
    const prevBtn = this.container.querySelector('.prev-week-btn');
    const nextBtn = this.container.querySelector('.next-week-btn');
    console.log('WeeklyCalendar: Navigation buttons created:', {
      prevBtn: !!prevBtn,
      nextBtn: !!nextBtn,
    });
  }

  renderDayCard(date, index) {
    const isToday = this.isToday(date);
    const isSelected = this.isSelected(date);
    const dayEvents = this.getEventsForDate(date);
    const dayTasks = this.getTasksForDate(date);

    return `
      <div class="day-card ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}" data-date="${this.formatDate(date)}">
        <div class="day-header">
          <div class="day-name">${this.getDayName(date)}</div>
          <div class="day-number">${date.getDate()}</div>
        </div>
        
        <div class="day-events">
          ${
            dayEvents.length > 0
              ? dayEvents
                  .map(
                    (event) => `
              <div class="event-item ${event.priority || 'medium'}-priority" title="${event.title}">
                ${event.title}
              </div>
            `
                  )
                  .join('')
              : '<div class="no-events">No events</div>'
          }
          
          ${
            dayTasks.length > 0
              ? dayTasks
                  .map(
                    (task) => `
              <div class="event-item task-item ${task.priority || 'medium'}-priority" title="${task.title}">
                📋 ${task.title}
              </div>
            `
                  )
                  .join('')
              : ''
          }
        </div>
        
        <button class="add-event-btn" data-date="${this.formatDate(date)}" title="Add event">
          +
        </button>
      </div>
    `;
  }

  getWeekDates(centerDate) {
    const dates = [];
    const startOfWeek = new Date(centerDate);
    startOfWeek.setDate(centerDate.getDate() - centerDate.getDay());

    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      dates.push(date);
    }

    return dates;
  }

  formatWeekRange(startDate, endDate) {
    const start = this.formatDateShort(startDate);
    const end = this.formatDateShort(endDate);
    return `${start} - ${end}`;
  }

  formatDate(date) {
    return date.toISOString().split('T')[0];
  }

  formatDateShort(date) {
    const months = [
      'JAN',
      'FEB',
      'MAR',
      'APR',
      'MAY',
      'JUN',
      'JUL',
      'AUG',
      'SEP',
      'OCT',
      'NOV',
      'DEC',
    ];
    return `${months[date.getMonth()]} ${date.getDate()}`;
  }

  getDayName(date) {
    const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
    return days[date.getDay()];
  }

  isToday(date) {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }

  isSelected(date) {
    // For now, just highlight today
    return this.isToday(date);
  }

  getEventsForDate(date) {
    const dateStr = this.formatDate(date);
    return this.events.filter((event) => {
      const eventDate = new Date(event.start || event.dueDate);
      return eventDate.toDateString() === date.toDateString();
    });
  }

  getTasksForDate(date) {
    const dateStr = this.formatDate(date);
    return this.tasks.filter((task) => {
      if (!task.dueDate) return false;
      const taskDate = new Date(task.dueDate);
      return taskDate.toDateString() === date.toDateString();
    });
  }

  async loadEvents() {
    try {
      const localEvents = this.data.get('calendar_events', []);
      this.events = Array.isArray(localEvents) ? [...localEvents] : [];

      const capacityTasks = this.data.get('enoughTasks', []);
      this.tasks = Array.isArray(capacityTasks)
        ? capacityTasks.filter((task) => Boolean(task?.dueDate))
        : [];

      this.calendarInsights = this.calculateCalendarSummary(this.events);

      this.render();
    } catch (error) {
      console.error('Failed to load events:', error);
    }
  }

  attachEventListeners() {
    // Week navigation
    const prevBtn = this.container.querySelector('.prev-week-btn');
    const nextBtn = this.container.querySelector('.next-week-btn');

    console.log('Navigation buttons found:', { prevBtn, nextBtn });

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        console.log('Previous week clicked');
        this.goToPreviousWeek();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        console.log('Next week clicked');
        this.goToNextWeek();
      });
    }

    // Plan button
    const planBtn = this.container.querySelector('.plan-btn');
    if (planBtn) {
      planBtn.addEventListener('click', () => this.openPlanModal());
    }

    // Day card clicks
    const dayCards = this.container.querySelectorAll('.day-card');
    dayCards.forEach((card) => {
      card.addEventListener('click', (e) => {
        if (!e.target.classList.contains('add-event-btn')) {
          this.selectDay(card.dataset.date);
        }
      });
    });

    // Add event buttons
    const addBtns = this.container.querySelectorAll('.add-event-btn');
    addBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.addEvent(btn.dataset.date);
      });
    });
  }

  goToPreviousWeek() {
    const newWeek = new Date(this.currentWeek);
    newWeek.setDate(newWeek.getDate() - 7);
    this.currentWeek = newWeek;
    this.render();
    this.loadEvents();
  }

  goToNextWeek() {
    const newWeek = new Date(this.currentWeek);
    newWeek.setDate(newWeek.getDate() + 7);
    this.currentWeek = newWeek;
    this.render();
    this.loadEvents();
  }

  selectDay(dateStr) {
    // Remove previous selection
    const selected = this.container.querySelector('.day-card.selected');
    if (selected) {
      selected.classList.remove('selected');
    }

    // Add selection to clicked day
    const dayCard = this.container.querySelector(`[data-date="${dateStr}"]`);
    if (dayCard) {
      dayCard.classList.add('selected');
    }

    // Store selected date
    this.data.set('selected_date', dateStr);
  }

  renderCalendarInsights() {
    if (!this.calendarInsights) return '';

    const nextEvent = this.calendarInsights.nextEvent;
    const conflict = this.calendarInsights.conflictWarning;

    const upcomingMarkup = nextEvent
      ? `<div class="calendar-insight__primary">
            <span class="calendar-insight__label">Next event</span>
            <span class="calendar-insight__value">${this.formatCountdown(nextEvent.start)} · ${this.formatEventTitle(nextEvent.summary)}</span>
         </div>`
      : `<div class="calendar-insight__primary">
            <span class="calendar-insight__label">Next event</span>
            <span class="calendar-insight__value">No upcoming events</span>
         </div>`;

    const focusMarkup = `<div class="calendar-insight__secondary">
        <span class="calendar-insight__label">Focus buffer</span>
        <span class="calendar-insight__value">${this.formatDurationMinutes(this.calendarInsights.availableFocusMinutes)} free</span>
      </div>`;

    const conflictBanner = conflict
      ? `<div class="calendar-conflict-banner" role="alert">
            <span class="banner-icon">⚠️</span>
            <div>
              <strong>Calendar is packed.</strong> Only ${this.formatDurationMinutes(this.calendarInsights.availableFocusMinutes)} focus time left today.
            </div>
         </div>`
      : '';

    return `
      <div class="calendar-insights">
        <div class="calendar-insights__row">
          ${upcomingMarkup}
          ${focusMarkup}
        </div>
        ${conflictBanner}
      </div>
    `;
  }

  calculateCalendarSummary(events) {
    const baselineFocusMinutes = 360;
    const now = new Date();
    const todayKey = this.formatDate(now);

    const todaysEvents = Array.isArray(events)
      ? events
          .map((event) => this.normalizeEventForSummary(event))
          .filter((event) => event && event.dateKey === todayKey)
      : [];

    const busyMinutes = todaysEvents.reduce((total, event) => total + event.durationMinutes, 0);
    const availableFocusMinutes = Math.max(0, baselineFocusMinutes - busyMinutes);
    const nextEvent = this.findNextEvent(events, now);

    return {
      totalEvents: todaysEvents.length,
      busyMinutes,
      availableFocusMinutes,
      focusMinutes: baselineFocusMinutes,
      conflictWarning: availableFocusMinutes < baselineFocusMinutes * 0.4,
      nextEvent,
    };
  }

  normalizeEventForSummary(event) {
    if (!event) return null;
    const start = this.toDate(event.start);
    const end = this.toDate(event.end);
    if (!start || !end) return null;
    const durationMinutes = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
    return {
      start,
      end,
      durationMinutes,
      dateKey: this.formatDate(start),
      summary: event.summary,
    };
  }

  findNextEvent(events, now = new Date()) {
    if (!Array.isArray(events)) return null;
    const upcoming = events
      .map((event) => ({ ...event, startDate: this.toDate(event.start) }))
      .filter((event) => event.startDate && event.startDate >= now)
      .sort((a, b) => a.startDate - b.startDate);
    return upcoming.length > 0 ? upcoming[0] : null;
  }

  toDate(value) {
    if (!value) return null;
    if (value instanceof Date) return value;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  formatCountdown(dateLike) {
    const date = this.toDate(dateLike);
    if (!date) return 'Soon';
    const diffMs = date.getTime() - Date.now();
    if (diffMs <= 0) return 'Now';
    const diffMinutes = Math.round(diffMs / 60000);
    if (diffMinutes < 60) {
      return `${diffMinutes} min`;
    }
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    if (hours >= 6) {
      return date.toLocaleString(undefined, {
        weekday: 'short',
        hour: 'numeric',
        minute: '2-digit',
      });
    }
    if (minutes === 0) {
      return `${hours} hr`;
    }
    return `${hours}h ${minutes}m`;
  }

  formatDurationMinutes(minutes) {
    if (!Number.isFinite(minutes) || minutes <= 0) return '0 min';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainder = minutes % 60;
    if (remainder === 0) return `${hours} hr${hours === 1 ? '' : 's'}`;
    return `${hours}h ${remainder}m`;
  }

  formatEventTitle(title) {
    if (!title) return '(No title)';
    return title.length > 60 ? `${title.slice(0, 57)}…` : title;
  }

  openPlanModal() {
    // Create a simple planning modal
    const modal = document.createElement('div');
    modal.className = 'plan-modal';
    modal.innerHTML = `
      <div class="modal-backdrop"></div>
      <div class="modal-content">
        <div class="modal-header">
          <h3>Plan Focus Sessions</h3>
          <button class="modal-close">×</button>
        </div>
        <div class="modal-body">
          <p>Plan your focus sessions for the week ahead. This will help you stay on track with your goals.</p>
          <div class="plan-options">
            <button class="btn btn--ghost" id="add-focus-session">Add Focus Session</button>
          </div>
        </div>
      </div>
    `;

    // Add styles
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 1000;
      display: flex;
      align-items: center;
      justify-content: center;
    `;

    document.body.appendChild(modal);

    // Event listeners
    modal.querySelector('.modal-close').addEventListener('click', () => {
      document.body.removeChild(modal);
    });

    modal.querySelector('.modal-backdrop').addEventListener('click', () => {
      document.body.removeChild(modal);
    });

    modal.querySelector('#add-focus-session').addEventListener('click', () => {
      const newSession = prompt('What focus session would you like to schedule?');
      if (newSession) {
        const today = new Date();
        const event = {
          id: Date.now(),
          title: newSession,
          start: today.toISOString(),
          priority: 'medium',
          provider: 'local',
        };

        const events = this.data.get('calendar_events', []);
        const nextEvents = Array.isArray(events) ? [...events, event] : [event];
        this.data.set('calendar_events', nextEvents);
      }
      document.body.removeChild(modal);
    });
  }

  addEvent(dateStr) {
    const title = prompt('Enter event title:');
    if (title) {
      const event = {
        id: Date.now(),
        title: title,
        start: dateStr,
        priority: 'medium',
        provider: 'local',
      };

      const events = this.data.get('calendar_events', []);
      events.push(event);
      this.data.set('calendar_events', events);

      this.events = events;
      this.render();
    }
  }

  // Public method to refresh events
  refresh() {
    this.loadEvents();
  }
}
